<script setup lang="ts">
import { Button } from '@clovyr/bed';
import { ButtonShape } from '@clovyr/bed/components/controls/types';
import { deleteMessage, fetchMail } from '@clovyr/pollen/mail';
import type { Email } from '@clovyr/pollen/mail/types';
import { addScheme } from '@clovyr/pollen/remote_exec';

import ArrowBackIcon from '@/components/icons/ArrowBackIcon.vue';
import { useAppStatus } from '@/composables/useAppStatus';
import { useRemoteExec } from '@/composables/useRemoteExec';

import CloseIcon from '../../components/icons/CloseIcon.vue';
import RefreshIcon from '../../components/icons/RefreshIcon.vue';

import AppMailMessageItem from './AppMailMessageItem.vue';

interface LibraryAppDetail {
  id: string;
}

const props = defineProps<LibraryAppDetail>();

// assume that by the time we reach here, the deployment ID has been validated by the route guard

const deploymentID = toRef(props, 'id');
const { deployment, isRunning } = useAppStatus(deploymentID);

const emails = ref<Email[]>([]);

async function refreshMail() {
  if (!isRunning.value) {
    emails.value = [];
    return;
  }

  const { creds, ensureHostLogin } = useRemoteExec(deployment.value);
  if (!(await ensureHostLogin())) {
    emails.value = [];
    return;
  }

  emails.value = await fetchMail(addScheme(creds.value.host), creds.value.accessKey);
}
void refreshMail(); // once on load

const openedMsg = ref<Email>();

const onClickRefresh = () => {
  void refreshMail();
};

const onClickViewMsg = (msg) => {
  msg.read = true;
  openedMsg.value = msg;
};

const onClickDeleteMsg = (msg) => {
  if (!isRunning.value) {
    return;
  }

  const { creds, ensureHostLogin } = useRemoteExec(deployment.value);
  void ensureHostLogin().then((ready) => {
    if (ready) {
      void deleteMessage(addScheme(creds.value.host), creds.value.accessKey, msg.id);
    }
  });
  emails.value = emails.value.filter((m) => m.id !== msg.id);
};

const onClickCloseMsg = () => {
  openedMsg.value = undefined;
};
</script>

<template>
  <div class="app-instance">
    <div class="app-instance__top">
      <div class="app-background"></div>

      <div class="grid">
        <div class="grid__col">
          <router-link
            :to="{ name: 'LibraryAppDetail', params: { id: deploymentID } }"
            class="app-instance__back text text--mono"
          >
            <ArrowBackIcon />
            Back to App
          </router-link>
        </div>
      </div>

      <div class="app-instance__hero">
        <div class="app-instance__hero__grid grid">
          <div class="app-instance__hero__title grid__col">
            <div class="app-header__heading-container">
              <h2 class="app-header__heading">Mailbox</h2>
            </div>
            <div class="app-header__preheading text text--color-grey-secondary">
              View email messages sent by {{ deployment.appName }}
            </div>
          </div>

          <div class="app-instance__hero__buttons grid__col">
            <Button v-if="!openedMsg" @click="onClickRefresh" :shape="ButtonShape.Circle" reversed>
              <RefreshIcon />
            </Button>
            <Button v-else @click="onClickCloseMsg" :shape="ButtonShape.Circle">
              <CloseIcon />
            </Button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!!openedMsg" class="grid">
      <div class="grid__col">
        <div class="msg">
          <div class="msg__body">
            <div class="msg-heading">
              <span>From:</span><span>{{ openedMsg.from }}</span>
            </div>
            <div class="msg-heading">
              <span>To:</span><span>{{ openedMsg.to }}</span>
            </div>
            <div class="msg-heading">
              <span>Subject:</span><span>{{ openedMsg.subject }}</span>
            </div>
            <div class="msg-heading">{{ openedMsg.date }}</div>
          </div>
        </div>
        <div class="msg">
          <div v-if="openedMsg.contentType == 'text/html'" class="msg__body">
            <iframe :srcdoc="openedMsg.body" id="frame"></iframe>
          </div>
          <div v-else>
            <pre>
              {{ openedMsg.body }}
            </pre>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="grid">
      <div class="grid__col">
        <div v-if="emails.length">
          <AppMailMessageItem
            v-for="msg in emails"
            :key="msg.id"
            :msg="msg"
            @view="() => onClickViewMsg(msg)"
            @delete="() => onClickDeleteMsg(msg)"
          />
        </div>
        <AppMailMessageItem v-else />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use 'sass:color';
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/layout/grid-vars';
@use 'styles/core/responsive';
@use 'styles/core/typography/fonts';
@use 'styles/core/util';

.msg {
  --msg-item-pad-vert: #{grid-vars.space(2)};
  --msg-item-pad-horiz: #{grid-vars.space(3)};
  --msg-item-margin-bottom: #{grid-vars.space(2)};
  --msg-item-border-radius: #{grid-vars.space(2)};

  iframe {
    width: 100%;
    height: 800px; // TODO: set dynamically?
  }

  @include util.margin(bottom, var(--msg-item-margin-bottom));
  @include util.margin(left right, calc(var(--msg-item-pad-horiz) * -1));

  background: linear-gradient(180deg, rgb(65 59 79 / 50%) 0%, #413b4f 91.78%);
  border-radius: var(--msg-item-border-radius);

  @supports (backdrop-filter: blur()) {
    backdrop-filter: blur(util.rem(18));
    /* stylelint-disable scss/no-global-function-names */
    background: linear-gradient(
      180deg,
      rgb(65 59 79 / 40%) 0%,
      color.adjust(#413b4f, $alpha: -0.4) 91.78%
    );
    /* stylelint-enable scss/no-global-function-names */
  }

  @include responsive.media-query(phone) {
    @include util.margin(left right, 0);
  }

  &__body {
    padding: var(--msg-item-pad-vert) var(--msg-item-pad-horiz);
    border-radius: 0 0 var(--msg-item-border-radius) var(--msg-item-border-radius);

    iframe {
      padding: 10px 0;
    }
  }

  &__ctas {
    @include util.flex($justify: flex-end);

    padding: 0 var(--module-pad-horiz) var(--module-pad-vert);

    .button {
      @include responsive.media-query(full-sm) {
        @include fonts.font-size(14, mono);
      }

      @include responsive.media-query(tablet) {
        @include fonts.font-size(12, mono);
      }
    }
  }

  .msg-heading {
    color: lightgray;
    padding: 1.5px;

    span:first-child {
      font-weight: bold;
      color: #fff;
      padding-right: grid-vars.space(0.75);
    }
  }

  .msg-heading:last-child {
    font-weight: bolder;
  }
}
</style>
